import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ShoppingBag, Truck, Shovel, Calendar, Phone, Mail } from 'lucide-react';
import { useLanguageStore } from '../store';
import { translations } from '../translations';
import { supabase } from '../lib/supabase';

interface OrderData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address?: string;
  delivery_date: string;
  requires_transport?: boolean;
  requires_planting?: boolean;
  pickup_in_person?: boolean;
  total_amount: number;
  transport_cost?: number;
  planting_cost?: number;
  payment_status?: string;
  is_wholesale?: boolean;
  order_items?: Array<{
    plant_name: string;
    height: string;
    quantity: number;
    price: number;
  }>;
}

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const language = useLanguageStore((state) => state.language);
  const t = translations[language];

  const [order, setOrder] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'paid' | 'failed' | 'pending'>('pending');

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      const { data } = await supabase
        .from('orders')
        .select('*, order_items(*)')
        .eq('stripe_session_id', sessionId)
        .maybeSingle();

      if (data) {
        setOrder(data);
        setPaymentStatus((data.payment_status as 'paid' | 'failed' | 'pending') || 'pending');
      }
      setLoading(false);
    };

    fetchOrder();

    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('orders')
        .select('payment_status')
        .eq('stripe_session_id', sessionId)
        .maybeSingle();

      if (data?.payment_status && data.payment_status !== 'pending') {
        setPaymentStatus(data.payment_status as 'paid' | 'failed');
        clearInterval(interval);
      }
    }, 2000);

    setTimeout(() => clearInterval(interval), 30000);

    return () => clearInterval(interval);
  }, [sessionId]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <Loader2 className="w-12 h-12 text-green-600 mx-auto mb-4 animate-spin" />
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'pl' ? 'Weryfikacja płatności...' : 'Verifying payment...'}
        </p>
      </div>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <div className="w-24 h-24 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-14 h-14 text-red-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {language === 'pl' ? 'Płatność nie powiodła się' : 'Payment failed'}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
              {language === 'pl'
                ? 'Niestety płatność za zamówienie nie została zrealizowana lub została anulowana. Możesz spróbować ponownie.'
                : 'Unfortunately the payment for your order was not completed or was cancelled. You can try again.'}
            </p>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-5 mb-8 text-left">
              <p className="text-red-700 dark:text-red-300 text-sm">
                {language === 'pl'
                  ? 'Jeśli masz pytania lub potrzebujesz pomocy, skontaktuj się z nami:'
                  : 'If you have questions or need help, contact us:'}
              </p>
              <p className="text-red-700 dark:text-red-300 text-sm mt-1 font-semibold">iglakmierz@gmail.com</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/checkout"
              className="inline-flex items-center justify-center bg-green-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors"
            >
              {language === 'pl' ? 'Spróbuj ponownie' : 'Try again'}
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              {language === 'pl' ? 'Strona główna' : 'Home'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'pending') {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <div className="max-w-xl mx-auto">
          <Loader2 className="w-12 h-12 text-green-600 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {language === 'pl' ? 'Przetwarzanie płatności...' : 'Processing payment...'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'pl'
              ? 'Proszę czekać, weryfikujemy Twoją płatność.'
              : 'Please wait, we are verifying your payment.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <div className="w-24 h-24 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-14 h-14 text-green-600" />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-3">
          {language === 'pl' ? 'Dziękujemy za zamówienie!' : t.success.title}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">
          {language === 'pl'
            ? 'Płatność przebiegła pomyślnie. Potwierdzenie zostało wysłane na Twój e-mail.'
            : 'Payment was successful. Confirmation has been sent to your email.'}
        </p>
        {order && (
          <p className="text-sm text-gray-500 dark:text-gray-500 font-mono">
            {language === 'pl' ? 'Nr zamówienia:' : 'Order number:'} {order.id.substring(0, 8).toUpperCase()}
          </p>
        )}
      </div>

      {order && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-green-600" />
              {language === 'pl' ? 'Zamówione produkty' : 'Ordered products'}
            </h2>
            <div className="space-y-3">
              {(order.order_items || []).map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-gray-700 dark:text-gray-300">
                    {item.plant_name} – {item.height} × {item.quantity}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {(item.price * item.quantity).toFixed(2)} zł
                  </span>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4 space-y-1">
              {(order.transport_cost || 0) > 0 && (
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>{language === 'pl' ? 'Transport' : 'Transport'}</span>
                  <span>{(order.transport_cost || 0).toFixed(2)} zł</span>
                </div>
              )}
              {(order.planting_cost || 0) > 0 && (
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>{language === 'pl' ? 'Sadzenie' : 'Planting'}</span>
                  <span>{(order.planting_cost || 0).toFixed(2)} zł</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold text-gray-900 dark:text-white pt-1">
                <span>{language === 'pl' ? 'Razem' : 'Total'}</span>
                <span className="text-green-600">{order.total_amount.toFixed(2)} zł</span>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-600" />
              {language === 'pl' ? 'Szczegóły dostawy' : 'Delivery details'}
            </h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                <Mail className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                <span>{order.email}</span>
              </div>
              <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                <Phone className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                <span>{order.phone}</span>
              </div>
              <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                <Truck className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                <span>
                  {order.pickup_in_person
                    ? (language === 'pl' ? 'Odbiór osobisty – Białogórne 5' : 'Personal pickup – Białogórne 5')
                    : order.address}
                </span>
              </div>
              <div className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                <Calendar className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                <span>
                  {language === 'pl' ? 'Preferowana data:' : 'Preferred date:'} {order.delivery_date}
                </span>
              </div>
              {order.requires_planting && (
                <div className="flex items-start gap-2 text-green-700 dark:text-green-400">
                  <Shovel className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{language === 'pl' ? 'Usługa sadzenia: Tak' : 'Planting service: Yes'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5 mb-8 text-center">
        <p className="text-green-800 dark:text-green-300 text-sm">
          {language === 'pl'
            ? 'Wkrótce skontaktujemy się z Tobą w celu ustalenia szczegółów realizacji zamówienia.'
            : 'We will contact you soon to confirm the order details.'}
        </p>
      </div>

      <div className="text-center">
        <Link
          to="/"
          className="inline-flex items-center justify-center bg-green-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors"
        >
          {t.success.backToHome}
        </Link>
      </div>
    </div>
  );
}
